// File: /var/www/app1/frontend/src/components/History.js

import React, { useState, useEffect } from "react";
import axios from "axios";

function History({ user }) {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Filter states
  const [filters, setFilters] = useState({
    workerNumber: "",
    startDate: "",
    endDate: "",
    month: "",
    year: new Date().getFullYear().toString(),
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const rowsPerPageOptions = [5, 10, 25, 50, 100, "All"];

  useEffect(() => {
    fetchHistory();
    if (user.role === "admin") {
      fetchWorkers();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [history, filters]);

  useEffect(() => {
    // Reset to first page when filters change
    setCurrentPage(1);
  }, [filters]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get("/api/submissions/history");
      setHistory(response.data.history || []);
      setError("");
    } catch (error) {
      console.error("Failed to fetch history:", error);
      setError("Failed to load submission history");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const response = await axios.get("/api/missionaries");
      setWorkers(response.data.missionaries || []);
    } catch (error) {
      console.error("Failed to fetch workers:", error);
    }
  };

  const applyFilters = () => {
    let filtered = [...history];

    // Filter by worker number
    if (filters.workerNumber) {
      filtered = filtered.filter(
        (item) => item.missionary_number === filters.workerNumber
      );
    }

    // Filter by date range
    if (filters.startDate) {
      filtered = filtered.filter(
        (item) => new Date(item.submission_date) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date
      filtered = filtered.filter(
        (item) => new Date(item.submission_date) <= endDate
      );
    }

    // Filter by month
    if (filters.month) {
      filtered = filtered.filter((item) => {
        const date = new Date(item.submission_date);
        return (date.getMonth() + 1).toString() === filters.month;
      });
    }

    // Filter by year
    if (filters.year) {
      filtered = filtered.filter((item) => {
        const date = new Date(item.submission_date);
        return date.getFullYear().toString() === filters.year;
      });
    }

    setFilteredHistory(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  const handleResetFilters = () => {
    setFilters({
      workerNumber: "",
      startDate: "",
      endDate: "",
      month: "",
      year: new Date().getFullYear().toString(),
    });
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value === "All" ? "All" : parseInt(value));
    setCurrentPage(1);
  };

  // Calculate pagination
  const totalRows = filteredHistory.length;
  const totalPages =
    rowsPerPage === "All" ? 1 : Math.ceil(totalRows / rowsPerPage);

  // Get paginated data
  const paginatedHistory =
    rowsPerPage === "All"
      ? filteredHistory
      : filteredHistory.slice(
          (currentPage - 1) * rowsPerPage,
          currentPage * rowsPerPage
        );

  // Pagination controls
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPreviousPage = () =>
    setCurrentPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () =>
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));

  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    });

    return rangeWithDots;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const exportToCSV = () => {
    const headers = [
      "Submission ID",
      "Worker Number",
      "Original #",
      "Submission Date",
      "Submission Time",
    ];
    const csvData = filteredHistory.map((item) => {
      const date = new Date(item.submission_date);
      return [
        item.id,
        item.missionary_number || "N/A",
        item.old_missionary_number || "",
        date.toLocaleDateString(),
        date.toLocaleTimeString(),
      ];
    });

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `submission_history_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const months = [
    { value: "", label: "All Months" },
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let year = 2024; year <= currentYear + 1; year++) {
    years.push(year.toString());
  }

  // Calculate statistics
  const totalSubmissions = filteredHistory.length;
  const uniqueWorkers = new Set(
    filteredHistory.map((h) => h.missionary_number || h.worker_id)
  ).size;

  // Group by month for current year
  const currentYearSubmissions = filteredHistory.filter((item) => {
    const date = new Date(item.submission_date);
    return date.getFullYear() === new Date().getFullYear();
  });

  const submissionsThisMonth = filteredHistory.filter((item) => {
    const date = new Date(item.submission_date);
    const now = new Date();
    return (
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear()
    );
  }).length;

  if (loading) {
    return <div className="loading">Loading submission history...</div>;
  }

  return (
    <div className="history-container">
      <h2>Submission History</h2>

      {/* Statistics Cards */}
      <div className="history-stats">
        <div className="stat-card">
          <h4>Total Submissions</h4>
          <p className="stat-number">{totalSubmissions}</p>
        </div>
        <div className="stat-card">
          <h4>Unique Workers</h4>
          <p className="stat-number">{uniqueWorkers}</p>
        </div>
        <div className="stat-card">
          <h4>This Month</h4>
          <p className="stat-number">{submissionsThisMonth}</p>
        </div>
        <div className="stat-card">
          <h4>This Year</h4>
          <p className="stat-number">{currentYearSubmissions.length}</p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <h3>Filter History</h3>
        <div className="filter-row">
          {user.role === "admin" && (
            <div className="filter-group">
              <label>Worker:</label>
              <select
                value={filters.workerNumber}
                onChange={(e) =>
                  handleFilterChange("workerNumber", e.target.value)
                }
              >
                <option value="">All Workers</option>
                {workers.map((worker) => (
                  <option key={worker.id} value={worker.missionary_number}>
                    {worker.missionary_number}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Year:</label>
            <select
              value={filters.year}
              onChange={(e) => handleFilterChange("year", e.target.value)}
            >
              <option value="">All Years</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Month:</label>
            <select
              value={filters.month}
              onChange={(e) => handleFilterChange("month", e.target.value)}
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
            />
          </div>

          <div className="filter-actions">
            <button onClick={handleResetFilters} className="reset-btn">
              Reset Filters
            </button>
            <button onClick={exportToCSV} className="export-btn">
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="error-message">{error}</div>}

      {/* History Table */}
      <div className="history-table-container">
        {/* Pagination Controls - Top */}
        <div className="pagination-controls-top">
          <div className="rows-per-page">
            <label>Rows per page:</label>
            <select
              value={rowsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="showing-info">
            {totalRows > 0 && (
              <span>
                Showing{" "}
                {(currentPage - 1) *
                  (rowsPerPage === "All" ? totalRows : rowsPerPage) +
                  1}{" "}
                to{" "}
                {Math.min(
                  currentPage *
                    (rowsPerPage === "All" ? totalRows : rowsPerPage),
                  totalRows
                )}{" "}
                of {totalRows} entries
              </span>
            )}
          </div>
        </div>

        <table className="history-table">
          <thead>
            <tr>
              <th>Submission ID</th>
              {user.role === "admin" && <th>Worker Number</th>}
              {user.role === "admin" && <th>Original #</th>}
              <th>Submission Date</th>
              <th>Submission Time</th>
              <th>Day of Week</th>
            </tr>
          </thead>
          <tbody>
            {paginatedHistory.length === 0 ? (
              <tr>
                <td
                  colSpan={user.role === "admin" ? "6" : "4"}
                  className="no-data"
                >
                  {history.length === 0
                    ? "No submission history found"
                    : "No submissions match your filters"}
                </td>
              </tr>
            ) : (
              paginatedHistory.map((item) => {
                const date = new Date(item.submission_date);
                const dayOfWeek = date.toLocaleDateString("en-US", {
                  weekday: "long",
                });

                return (
                  <tr key={item.id}>
                    <td>#{item.id}</td>
                    {user.role === "admin" && (
                      <td>
                        <strong>{item.missionary_number || "N/A"}</strong>
                      </td>
                    )}
                    {user.role === "admin" && (
                      <td>{item.old_missionary_number || "—"}</td>
                    )}
                    <td>{date.toLocaleDateString()}</td>
                    <td>{date.toLocaleTimeString()}</td>
                    <td>{dayOfWeek}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination Controls - Bottom */}
        {totalPages > 1 && (
          <div className="pagination-controls">
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ««
            </button>
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              ‹
            </button>

            {getPaginationRange().map((page, index) =>
              page === "..." ? (
                <span key={`dots-${index}`} className="pagination-dots">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`pagination-btn ${
                    currentPage === page ? "active" : ""
                  }`}
                >
                  {page}
                </button>
              )
            )}

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              ›
            </button>
            <button
              onClick={goToLastPage}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              »»
            </button>
          </div>
        )}
      </div>

      {/* Summary */}
      {paginatedHistory.length > 0 && (
        <div className="history-summary">
          <p>
            Page {currentPage} of {totalPages} | Total: {filteredHistory.length}{" "}
            submission{filteredHistory.length !== 1 ? "s" : ""}
          </p>
        </div>
      )}

      {/* Styles */}
      <style jsx>{`
        .history-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .history-container h2 {
          color: white;
          margin-bottom: 30px;
          font-size: 32px;
        }

        .history-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px;
          border-radius: 8px;
          text-align: center;
        }

        .stat-card h4 {
          margin-bottom: 10px;
          font-size: 14px;
          opacity: 0.9;
        }

        .stat-number {
          font-size: 36px;
          font-weight: bold;
        }

        .filters-section {
          background: white;
          border-radius: 10px;
          padding: 25px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .filters-section h3 {
          color: #4a5568;
          margin-bottom: 20px;
        }

        .filter-row {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
          align-items: flex-end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          min-width: 150px;
        }

        .filter-group label {
          margin-bottom: 5px;
          color: #555;
          font-weight: 500;
          font-size: 14px;
        }

        .filter-group input,
        .filter-group select {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }

        .filter-actions {
          display: flex;
          gap: 10px;
          align-items: flex-end;
        }

        .reset-btn {
          padding: 8px 16px;
          background-color: #718096;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .reset-btn:hover {
          background-color: #4a5568;
        }

        .export-btn {
          padding: 8px 16px;
          background-color: #48bb78;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .export-btn:hover {
          background-color: #38a169;
        }

        .history-table-container {
          background: white;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .history-table {
          width: 100%;
          border-collapse: collapse;
        }

        .history-table th {
          background-color: #f7fafc;
          padding: 12px;
          text-align: left;
          font-weight: 600;
          color: #4a5568;
          border-bottom: 2px solid #e2e8f0;
        }

        .history-table td {
          padding: 12px;
          border-bottom: 1px solid #e2e8f0;
          color: #4a5568;
        }

        .history-table tr:hover {
          background-color: #f7fafc;
        }

        .no-data {
          text-align: center;
          color: #a0aec0;
          font-style: italic;
        }

        .history-summary {
          margin-top: 20px;
          text-align: center;
          color: white;
          font-size: 14px;
        }

        /* Pagination Styles */
        .pagination-controls-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background-color: #f7fafc;
          border-bottom: 1px solid #e2e8f0;
        }

        .rows-per-page {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .rows-per-page label {
          color: #4a5568;
          font-size: 14px;
        }

        .rows-per-page select {
          padding: 5px 10px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          background: white;
        }

        .showing-info {
          color: #718096;
          font-size: 14px;
        }

        .pagination-controls {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 5px;
          padding: 20px;
          background-color: #f7fafc;
          border-top: 1px solid #e2e8f0;
        }

        .pagination-btn {
          padding: 8px 12px;
          background-color: white;
          color: #4a5568;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
          min-width: 35px;
        }

        .pagination-btn:hover:not(:disabled) {
          background-color: #667eea;
          color: white;
          border-color: #667eea;
        }

        .pagination-btn.active {
          background-color: #667eea;
          color: white;
          border-color: #667eea;
        }

        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination-dots {
          padding: 8px 5px;
          color: #718096;
        }

        @media (max-width: 768px) {
          .filter-row {
            flex-direction: column;
          }

          .filter-group {
            width: 100%;
          }

          .pagination-controls-top {
            flex-direction: column;
            gap: 10px;
            align-items: flex-start;
          }

          .pagination-controls {
            flex-wrap: wrap;
          }

          .pagination-btn {
            padding: 6px 10px;
            min-width: 30px;
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
}

export default History;
